// Секция «Показы» для отдельной tracking-link. Большое последнее
// значение + кнопка «Обновить» (модалка с инпутом и заметкой) +
// история записей. Источник «истины» для конверсии impressions →
// visits → clicks в воронке выше.

import { useMemo, useState, type FormEvent } from 'react';
import { Eye, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useImpressions, useRecordImpression } from '../../lib/hooks.ts';
import { ApiError } from '../../lib/api.ts';

interface Props {
  linkId: string;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

export function ImpressionsSection({ linkId }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const history = useImpressions(linkId);
  const items = history.data?.items ?? [];
  const latest = items[0];

  return (
    <div className="rounded-lg border border-border bg-bg-soft p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-fg-muted" />
          <span className="text-sm font-medium uppercase tracking-wider text-fg-muted">Показы</span>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          {latest ? 'Обновить' : 'Задать'}
        </Button>
      </div>

      {history.isLoading ? (
        <div className="h-8 w-32 animate-pulse rounded bg-bg-elevated" />
      ) : latest ? (
        <>
          <div className="flex items-baseline gap-3">
            <div className="text-3xl font-semibold tabular-nums">
              {formatNumber(latest.totalImpressions)}
            </div>
            <div className="text-xs text-fg-muted">
              обновлено {formatDateTime(latest.recordedAt)}
            </div>
          </div>
          {items.length > 1 ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-fg-muted hover:text-fg">
                История ({items.length})
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-bg-elevated text-fg-muted">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Когда</th>
                      <th className="px-2 py-1.5 text-right font-medium">Значение</th>
                      <th className="px-2 py-1.5 text-left font-medium">Заметка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((rec) => (
                      <tr key={rec.id} className="border-t border-border">
                        <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-fg-muted">
                          {formatDateTime(rec.recordedAt)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums">
                          {formatNumber(rec.totalImpressions)}
                        </td>
                        <td className="px-2 py-1.5 text-fg-muted">{rec.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}
        </>
      ) : (
        <div className="text-sm text-fg-muted">
          Введи количество показов из IG/TT/другого источника, чтобы дашборд считал конверсию.
        </div>
      )}

      <ImpressionDialog
        open={open}
        onOpenChange={setOpen}
        linkId={linkId}
        previousValue={latest?.totalImpressions}
      />
    </div>
  );
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linkId: string;
  previousValue: number | undefined;
}

function ImpressionDialog({ open, onOpenChange, linkId, previousValue }: DialogProps): JSX.Element {
  const [total, setTotal] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const record = useRecordImpression(linkId);

  const parsed = useMemo(() => {
    const trimmed = total.trim();
    if (!trimmed) return null;
    const n = Number(trimmed.replace(/\s+/g, ''));
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return NaN;
    return n;
  }, [total]);

  function reset(): void {
    setTotal('');
    setNote('');
    setError(null);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (parsed === null || Number.isNaN(parsed)) {
      setError('Введи целое число ≥ 0');
      return;
    }
    try {
      await record.mutateAsync({
        total_impressions: parsed,
        note: note.trim() ? note.trim() : null,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError('Значение вне допустимых границ (0 … 10^12)');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось сохранить');
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogTitle>Обновить количество показов</DialogTitle>
        <DialogDescription>
          {previousValue !== undefined
            ? `Текущее значение: ${formatNumber(previousValue)}. Введи новое суммарное число показов (не дельту).`
            : 'Введи суммарное число показов этого источника на сегодня.'}
        </DialogDescription>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <div>
            <Label htmlFor="total">Всего показов</Label>
            <Input
              id="total"
              type="text"
              inputMode="numeric"
              autoFocus
              placeholder="например, 12500"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="note">
              Заметка <span className="text-fg-muted">(опционально)</span>
            </Label>
            <Input
              id="note"
              type="text"
              maxLength={200}
              placeholder="например, «после второй сторис»"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {error ? <div className="text-sm text-red-500">{error}</div> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" disabled={record.isPending}>
              {record.isPending ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
